import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { HeroSection } from '../components/home/HeroSection';
import { ServicesPreview } from '../components/home/ServicesPreview';
import { BenefitsSection } from '../components/home/BenefitsSection';
import { TherapistSpotlight } from '../components/home/TherapistSpotlight';
import { TestimonialsSection } from '../components/home/TestimonialsSection';
import { BookingCTA } from '../components/home/BookingCTA';
import { FAQSection } from '../components/home/FAQSection';
import { Layout } from '../components/layout';

/**
 * HomePage component - Main landing page for Massatherapy
 *
 * Assembles all homepage sections in the correct order:
 * 1. HeroSection - Full viewport hero with CTAs
 * 2. ServicesPreview - Overview of available massage services
 * 3. BenefitsSection - Why choose us / benefits grid
 * 4. TherapistSpotlight - Meet the team section
 * 5. TestimonialsSection - Client reviews carousel
 * 6. BookingCTA - Call to action for booking
 * 7. FAQSection - Frequently asked questions
 * 8. Footer - Site footer with links and contact info
 */
export const HomePage: FC = () => {
  return (
    <Layout hasHeaderPadding={false}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Massatherapy | Professional Massage Therapy Services</title>
        <meta
          name="description"
          content="Experience the healing power of professional massage therapy. Our licensed therapists offer Swedish, deep tissue, hot stone, and specialty massages tailored to your wellness needs. Book your session today."
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Massatherapy | Professional Massage Therapy Services" />
        <meta
          property="og:description"
          content="Experience the healing power of professional massage therapy. Our licensed therapists offer Swedish, deep tissue, hot stone, and specialty massages tailored to your wellness needs."
        />
        <meta property="og:site_name" content="Massatherapy" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Massatherapy | Professional Massage Therapy Services" />
        <meta
          name="twitter:description"
          content="Experience the healing power of professional massage therapy. Our licensed therapists offer Swedish, deep tissue, hot stone, and specialty massages tailored to your wellness needs."
        />

        {/* Additional SEO */}
        <meta
          name="keywords"
          content="massage therapy, Swedish massage, deep tissue massage, hot stone therapy, sports massage, prenatal massage, wellness, relaxation, therapeutic massage"
        />
        <link rel="canonical" href="https://massatherapy.com" />
      </Helmet>

      {/* Hero Section - Full viewport height landing */}
      <div id="hero">
        <HeroSection />
      </div>

      {/* Services Preview - What we offer */}
      <div id="services">
        <ServicesPreview />
      </div>

      {/* Benefits Section - Why choose us */}
      <div id="benefits">
        <BenefitsSection />
      </div>

      {/* Therapist Spotlight - Meet the team */}
      <div id="therapists">
        <TherapistSpotlight />
      </div>

      {/* Testimonials - Client reviews */}
      <div id="testimonials">
        <TestimonialsSection />
      </div>

      {/* Booking CTA - Call to action */}
      <div id="book">
        <BookingCTA />
      </div>

      {/* FAQ Section - Common questions */}
      <div id="faq">
        <FAQSection />
      </div>
    </Layout>
  );
};

HomePage.displayName = 'HomePage';
